import { CredentialsManager, Credentials, Profile } from './credentials.js'
import { AuthService } from './service.js'

export interface LoginOptions {
  profile?: string
  apiUrl?: string
}

export interface AuthAPI {
  login(token: string, options?: LoginOptions): Promise<void>
  logout(profile?: string): Promise<void>
  listProfiles(): Promise<Profile[]>
  getCurrentProfile(): Promise<string>
  getCurrentProfileDetails(): Promise<Profile | null>
  setCurrentProfile(profileName: string): Promise<void>
  getActiveCredentials(): Promise<Credentials>
  validateToken(token: string, apiUrl?: string): Promise<boolean>
}

class Auth implements AuthAPI {
  private credentialsManager: CredentialsManager

  constructor() {
    this.credentialsManager = new CredentialsManager()
  }

  async login(token: string, options: LoginOptions = {}): Promise<void> {
    const { profile = 'default', apiUrl = 'https://api.botpress.cloud' } = options

    // Validate the token first
    const authService = new AuthService(apiUrl)
    const authResult = await authService.validateToken(token)

    // Save credentials with user info
    await this.credentialsManager.saveCredentials(
      profile,
      {
        token,
        apiUrl,
        workspaceId: authResult.workspaceId,
        workspaceName: authResult.workspaceName,
        botId: authResult.botId,
      },
      {
        email: authResult.email,
        displayName: authResult.displayName,
        accountId: authResult.accountId,
        createdAt: authResult.createdAt,
      }
    )
  }

  async logout(profile?: string): Promise<void> {
    if (profile) {
      await this.credentialsManager.deleteProfile(profile)
    } else {
      // Delete current profile
      const currentProfile = await this.credentialsManager.getCurrentProfile()
      await this.credentialsManager.deleteProfile(currentProfile)
    }
  }

  async listProfiles(): Promise<Profile[]> {
    return this.credentialsManager.listProfiles()
  }

  async getCurrentProfile(): Promise<string> {
    return this.credentialsManager.getCurrentProfile()
  }

  async getCurrentProfileDetails(): Promise<Profile | null> {
    return this.credentialsManager.getCurrentProfileDetails()
  }

  async setCurrentProfile(profileName: string): Promise<void> {
    return this.credentialsManager.setCurrentProfile(profileName)
  }

  async getActiveCredentials(): Promise<Credentials> {
    return this.credentialsManager.getActiveCredentials()
  }

  async validateToken(token: string, apiUrl?: string): Promise<boolean> {
    const authService = new AuthService(apiUrl)
    return authService.testConnection(token)
  }
}

// Export singleton instance
export const auth = new Auth()

// Export types
export type { Credentials, Profile } from './credentials.js'
export type { AuthResult } from './service.js'

// Export classes
export { CredentialsManager } from './credentials.js'

// Export BP CLI importer
export { bpCliImporter } from './bp-cli-import.js'
